import {
    Component,
    ContentChild,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild
} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {BehaviorSubject, ReplaySubject, Subject, Subscription} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {SelectFilterElement} from './select-filter.model';
import {ContentDirective} from '../../../shared/directives/content.directive';
import {MatSelect} from '@angular/material/select';
import {FormUtils} from '../../../shared/utils/form.utils';
import {
    AbstractSelectComponent,
    NewItemModalOptions
} from '../../../modules/core/component/select/abstract-select.component';
import {DialogController} from '../../../modules/common/dialog/dialogController';


@Component({
    selector: 'app-select-filter',
    templateUrl: './select-filter.component.html',
    styleUrls: ['./select-filter.component.scss'],
    viewProviders: [DialogController]
})
// FIXME How to display error?
export class SelectFilterComponent<V, E> extends AbstractSelectComponent<V> implements OnInit, OnDestroy, OnChanges {

    @ContentChild(ContentDirective, { static: true, read: TemplateRef }) template: TemplateRef<any>;

    @Input() disabled = false;
    @Input() loading = false;
    @Input() parentFormGroup: UntypedFormGroup;
    @Input() noEntriesFoundLabel: string;
    @Input() placeHolderLabel: string;
    @Input() parentFormControlName: string;
    @Input() elements: BehaviorSubject<SelectFilterElement<V, E>[]> = new BehaviorSubject<SelectFilterElement<V, E>[]>([]);
    @Input() filterElementProcessor: (criteria: string, element: E) => boolean;
    @Input() creationEnable = false;
    @Input() deletable = false;
    @Input() noValueLabel: string;
    @Input() newItemModalOptions: NewItemModalOptions<any, any, any>;
    @Input() createButtonLabel: string;
    @Input() errorsMessages: {[key: string]: string};

    @Output() focussedEvent: EventEmitter<void> = new EventEmitter<void>();
    @Output() _onSingleSelectionChange = new EventEmitter<SelectFilterElement<V, E>>();

    @ViewChild('matSelect') matSelect: MatSelect;

    eraseSelectionEnabled = false;
    /** control for filter */
    filterCtrl: UntypedFormControl = new UntypedFormControl();
    /** Subject that emits when the component has been destroyed. */
    _onDestroy = new Subject<void>();
    filteredElements: ReplaySubject<SelectFilterElement<V, E>[]> = new ReplaySubject<SelectFilterElement<V, E>[]>(1);
    subscription: Subscription;

    constructor() {
        super();
    }


    ngOnInit() {
        this.elements
            .pipe(takeUntil(this._onDestroy))
            .subscribe(
            (value: any[]) => {
                this.filteredElements.next(value);
                const formValue = this.elementControl?.value;
                /* if (formValue) {
                    this.onSelectionChange(formValue);
                } */

            }
        );
        this.filterCtrl.valueChanges
            .pipe(takeUntil(this._onDestroy))
            .subscribe(criteria => {
                this.filter(criteria);
            });

        this.eraseSelectionEnabled = this.deletable && !FormUtils.isRequiredField(this.elementControl);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['parentFormGroup'] || changes['parentFormControlName']) {
            this.subscription?.unsubscribe();
            if (this.parentFormControlName && this.parentFormGroup) {
                this.subscription = this.parentFormGroup.controls[this.parentFormControlName].valueChanges.subscribe(value => {
                    // console.logByType'maxime value changed');
                    this.onSelectionChange(value);
                });
            }
        }
    }

    private filter(criteria: string) {
        if (!this.elements) {
            return;
        }
        if (!criteria) {
            this.filteredElements.next(this.elements.getValue());
            return;
        }
        this.filteredElements.next(
            this.elements.getValue().filter(element => this.filterElements(criteria, element.element))
        );
    }

    ngOnDestroy() {
        this._onDestroy.next();
        this._onDestroy.complete();
    }

    get elementControl() {
        return this.parentFormGroup.get(this.parentFormControlName);
    }

    getSelectedElement(): E {
        if (this.elementControl.value) {
            const element = this.elements.getValue().filter(item => item.value === this.elementControl.value);
            if (element && element.length === 1) {
                return element[0].element;
            }
        }
    }

    onSelectionChange(value: V) {
        if (value) {
            this._onSingleSelectionChange.next(this.elements.getValue().find(item => item.value === value));
        } else {
            this._onSingleSelectionChange.next(null);
        }
    }

    filterElements = (criteria: string, element: E) => {
        if (this.filterElementProcessor) {
            return this.filterElementProcessor(criteria, element);
        } else {
            return element.toString().replace(/<[^>]*>/g, '').toUpperCase().includes(criteria.toUpperCase());
        }
    }

    eraseSelection(){
        this.filterCtrl.setValue(null);
        this.elementControl.setValue(null);
        this._onSingleSelectionChange.emit(null);
    }

    addNewItem(value: any) {
        this.elementControl.setValue(value);
    }

    closeSelect() {
        this.matSelect.close();
    }

    getSelectInputValue(): string {
        return this.filterCtrl.value;
    }

    errorsKey(): string[] {
        const errors = FormUtils.collectErrors(this.elementControl);
        if (errors) {
            return Object.keys(errors);
        }
    }

    extractErrorMessage(errorKey: string) {
        if (this.errorsMessages && this.errorsMessages[errorKey]) {
            return this.errorsMessages[errorKey];
        }
        if (errorKey === 'required') {
            return 'La valeur est obligatoire';
        }
        return 'La valeur n\'est pas correcte';
    }

    focussed(){
        this.focussedEvent.emit();
    }
}
